import { fx } from './animation-preferences';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { breakpoint } from '../utilities/variables';

// Attributs pour les animations
export const ATTR_SCOPE = 'data-anim-scope';
export const ATTR_TARGET = 'data-anim';
export const ATTR_SPLIT = 'data-split';

// Attributs pour les parallax
export const ATTR_PARALLAX = 'data-parallax'; // valeur numérique (facteur) ex: 0.2
export const ATTR_PARALLAX_AXIS = 'data-parallax-axis'; // "y" (défaut) ou "x"
export const ATTR_PARALLAX_OFFSET = 'data-parallax-offset'; // valeur px (défaut: 100)
export const ATTR_PARALLAX_WRAP = 'data-parallax-wrap';
export const ATTR_PARALLAX_SCOPE = 'data-parallax-scope';
export const ATTR_PARALLAX_COVER = 'data-parallax-cover';

// Propriétés utilitaires GSAP
export const ANIM_CLEAR_PROPS = { clearProps: 'all', attr: { 'data-anim': 'on' } };
export const ANIM_DISABLED_STATE = { attr: { 'data-anim': '' } };

// Namespace global pour interagir facilement depuis n'importe où
if (!window.Anim) window.Anim = {};
if (!window.Anim.scroll) {
    window.Anim.scroll = {
        instances: [],
        /** tue tous les ScrollTriggers */
        killAll: () => ScrollTrigger.killAll(),
        /** force un refresh global des positions */
        refresh: () => ScrollTrigger.refresh(),
        update: () => ScrollTrigger.update(),
    };
}

/* Classe gérant la révélation au scroll + split text optionnel */
export class ScrollEffects {
    constructor(options) {
        this.defaults = {
            scope: `[${ATTR_SCOPE}]`,
            targets: `[${ATTR_TARGET}]`,
            stagger: 0.1,
            easing: 'back.out(1.7)',
            duration: 1.2,
            markers: false,
            effect: 'fade-up', // 'fade-up' | 'scale-up'
            ...options,
        };

        this.scopeEl =
            typeof this.defaults.scope === 'string'
                ? document.querySelector(this.defaults.scope)
                : this.defaults.scope;

        if (!this.scopeEl) return;

        this.start = this.scopeEl.getAttribute('data-anim-start') || 'top 90%';
        this.end = this.scopeEl.getAttribute('data-anim-end') || 'bottom 10%';
    }

    init() {
        this.initParallaxInside();

        if (!this.scopeEl) return;
        this.revealTargets(this.scopeEl);
        this.initSplitInside(this.scopeEl);
    }

    // Anime les cibles d'un scope
    revealTargets(scope) {
        const effect = scope.dataset.animEffect || this.defaults.effect; // 'fade-up' | 'scale-up'
        let childrenToAnimate = scope.matches(this.defaults.targets)
            ? [scope]
            : Array.from(scope.querySelectorAll(this.defaults.targets));

        // Cas particulier : contenu TinyMCE (wysiwyg)
        if (scope.classList.contains('tinymce')) {
            childrenToAnimate = Array.from(scope.children);
        }

        if (!childrenToAnimate.length) return;

        const initialValues = { opacity: 0 };

        switch (effect) {
            case 'scale-up':
                initialValues.scale = 0.5;
                break;
            default: // fade-up
                initialValues.y = 100;
        }

        fx.set(childrenToAnimate, initialValues);

        fx.to(childrenToAnimate, {
            y: 0,
            x: 0,
            scale: 1,
            opacity: 1,
            stagger: this.defaults.stagger,
            ease: this.defaults.easing,
            duration: this.defaults.duration,
            scrollTrigger: {
                trigger: scope,
                start: this.start,
                end: this.end,
                markers: this.defaults.markers,
                once: true,
            },
            onComplete: () => fx.set(childrenToAnimate, ANIM_CLEAR_PROPS),
        });
    }

    // Initialise l'effet SplitText (sans dépendre du plugin GSAP) pour les éléments ayant [data-split]
    // data-split="chars" | "words" | "lines" (défaut: chars)
    initSplitInside(scope) {
        const splitEls = scope.querySelectorAll(`[${ATTR_SPLIT}]`);
        if (!splitEls.length) return;

        splitEls.forEach((el) => this._setupSplitOnEnter(el));
    }

    // Initialise un effet parallax simple sur les éléments ayant [data-parallax]
    // data-parallax = facteur (nombre) — ex: 0.2
    // data-parallax-axis = "y" (défaut) ou "x"
    initParallaxInside() {
        const els = document.querySelectorAll(`[${ATTR_PARALLAX}]`);
        if (!els.length) return;

        els.forEach((el) => {
            this._initParallaxCover(el);
            this._initParallaxFactor(el);
        });
    }

    _initParallaxFactor(el) {
        const wrapper = el.closest(`[${ATTR_PARALLAX_SCOPE}]`);

        if (!wrapper) return;

        const factor = parseFloat(el.getAttribute(ATTR_PARALLAX));
        const axis = (el.getAttribute(ATTR_PARALLAX_AXIS) || 'y').toLowerCase();

        const k = isNaN(factor) ? 0.2 : factor;
        const key = (wrapper && wrapper.getAttribute('data-parallax-breakpoint')) || 'sidebar';
        const bpKey = Object.prototype.hasOwnProperty.call(breakpoint, key) ? key : 'sidebar';
        const mq = `(min-width: ${breakpoint[bpKey]}px)`;

        const triggerEl = wrapper || el;
        const range = () => window.innerHeight * k;
        const fromProps = axis === 'x' ? { x: () => range() } : { y: () => range() };
        const toProps =
            axis === 'x'
                ? { x: () => -range(), ease: 'none' }
                : { y: () => -range(), ease: 'none' };

        const mm = fx.matchMedia();

        if (!mm) return;

        mm.add(mq, () =>
            fx.fromTo(el, fromProps, {
                ...toProps,
                scrollTrigger: {
                    trigger: triggerEl,
                    start: 'top bottom',
                    end: 'bottom top',
                    scrub: true,
                    invalidateOnRefresh: true,
                    markers: this.defaults.markers,
                    onEnter: () => fx.set(el, { willChange: 'transform' }),
                    onEnterBack: () => fx.set(el, { willChange: 'transform' }),
                    onLeave: () => fx.set(el, { willChange: 'auto' }),
                    onLeaveBack: () => fx.set(el, { willChange: 'auto' }),
                    onKill: () => fx.set(el, { willChange: 'auto' }),
                },
            })
        );
    }

    _initParallaxCover(el) {
        const wrapper = el.closest(`[${ATTR_PARALLAX_COVER}]`);

        if (!wrapper) return;

        const offset = parseInt(el.getAttribute(ATTR_PARALLAX_OFFSET), 10);
        const coverOffset = isNaN(offset) ? 50 : offset;

        if (getComputedStyle(wrapper).position === 'static') {
            wrapper.style.position = 'relative';
        }

        wrapper.style.overflow = 'hidden';

        const adjustCoverSize = () => {
            el.style.position = 'absolute';
            el.style.inset = `-${coverOffset}px 0`;
            el.style.height = `calc(100% + ${coverOffset * 2}px)`;
            el.style.width = '100%';
            el.style.objectFit = 'cover';
        };

        adjustCoverSize();

        fx.fromTo(
            el,
            { y: coverOffset },
            {
                y: -coverOffset,
                ease: 'none',
                scrollTrigger: {
                    trigger: wrapper,
                    start: 'top bottom',
                    end: 'bottom top',
                    scrub: true,
                    invalidateOnRefresh: true,
                    markers: this.defaults.markers,
                    onRefresh: adjustCoverSize,
                    onEnter: () => fx.set(el, { willChange: 'transform' }),
                    onEnterBack: () => fx.set(el, { willChange: 'transform' }),
                    onLeave: () => fx.set(el, { willChange: 'auto' }),
                    onLeaveBack: () => fx.set(el, { willChange: 'auto' }),
                    onKill: () => fx.set(el, { willChange: 'auto' }),
                },
            }
        );
    }

    // Crée un ScrollTrigger sur l'élément, split le texte à l'entrée et l'anime
    _setupSplitOnEnter(el) {
        const mode = (el.getAttribute(ATTR_SPLIT) || 'chars').toLowerCase(); // 'chars' | 'words' | 'lines'

        // Préparation: on garde le HTML original (styles/markup conservés) + texte pour SR
        const originalHTML = el.innerHTML;
        const originalText = el.textContent;
        el.setAttribute('aria-label', (originalText || '').trim());

        // On crée le trigger calé sur le même start/end que le scope parent s'il existe
        const parentScope = el.closest(`[${ATTR_SCOPE}]`);
        const start = parentScope
            ? parentScope.getAttribute('data-anim-start') || 'top 90%'
            : 'top 90%';
        const end = parentScope
            ? parentScope.getAttribute('data-anim-end') || 'bottom 10%'
            : 'bottom 10%';

        ScrollTrigger.create({
            trigger: el,
            start,
            end,
            once: true,
            markers: this.defaults.markers,
            onEnter: () => {
                // Split (génère des inners transformables)
                const fragments = this._splitText(el, mode);

                // will-change appliqué uniquement pendant l'anim
                fx.set(fragments, { y: '1em', opacity: 0, willChange: 'transform, opacity' });

                // Le conteneur passe en état visible piloté par CSS si défini
                el.setAttribute('data-split', 'on');

                // Animation
                fx.to(fragments, {
                    y: 0,
                    opacity: 1,
                    ease: this.defaults.easing,
                    duration: this.defaults.duration,
                    stagger: 0.03,
                    onComplete: () => {
                        fx.set(fragments, { clearProps: 'all' });
                        // Revert: remettre le HTML original (conserve <strong>, styles, etc.)
                        el.innerHTML = originalHTML;
                        el.removeAttribute('aria-label');
                    },
                });
            },
        });
    }

    // Split text utilitaire simplifié: wrap selon mode:
    // - 'chars': chaque caractère non-blanc dans <span class="split-frag"><span class="split-frag-i">X</span></span>, espaces laissés tels quels
    // - 'words': chaque mot dans <span class="split-frag"><span class="split-frag-i">word</span></span>, espaces réinsérés comme noeuds texte
    // - 'lines': chaque ligne (séparée par \n ou <span class="br">) dans <span class="split-frag split-frag--line"><span class="split-frag-i">line</span></span>
    // Les éléments .br sont préservés et non traversés.
    _splitText(el, mode = 'chars') {
        // Nettoie un split précédent (au cas où)
        el.querySelectorAll('.split-frag, .split-frag-i').forEach((n) => n.remove());

        const frags = [];

        if (mode === 'lines') {
            // Pour 'lines', on split par noeuds texte sur \n et par éléments .br
            // On traite chaque ligne séparément, en conservant .br comme séparateurs

            const processLine = (lineNodes) => {
                // lineNodes est un tableau de noeuds (texte ou éléments) qui forment une ligne
                // On doit créer un wrapper .split-frag.split-frag--line et un inner .split-frag-i avec tout ce contenu

                const outer = document.createElement('span');
                outer.className = 'split-frag split-frag--line';

                const inner = document.createElement('span');
                inner.className = 'split-frag-i';
                inner.setAttribute('aria-hidden', 'true');

                lineNodes.forEach((node) => {
                    inner.appendChild(node);
                });

                outer.appendChild(inner);
                el.appendChild(outer);
                frags.push(inner);
            };

            // On vide el et reconstruisons
            const nodes = Array.from(el.childNodes);
            el.textContent = '';

            let lineBuffer = [];

            nodes.forEach((node) => {
                if (node.nodeType === Node.ELEMENT_NODE && node.classList.contains('br')) {
                    // fin de ligne avant le <span.br>
                    if (lineBuffer.length) {
                        processLine(lineBuffer);
                        lineBuffer = [];
                    }
                    // On remet le .br tel quel
                    el.appendChild(node);
                } else if (node.nodeType === Node.TEXT_NODE) {
                    // Split le texte sur \n
                    const parts = node.nodeValue.split('\n');
                    parts.forEach((part, i) => {
                        if (part.length > 0) {
                            lineBuffer.push(document.createTextNode(part));
                        }
                        if (i < parts.length - 1) {
                            // fin de ligne sur \n
                            if (lineBuffer.length) {
                                processLine(lineBuffer);
                                lineBuffer = [];
                            }
                        }
                    });
                } else {
                    // Élément normal, on ajoute au buffer
                    lineBuffer.push(node);
                }
            });

            // Dernière ligne
            if (lineBuffer.length) {
                processLine(lineBuffer);
                lineBuffer = [];
            }

            return frags;
        }

        // Fonction pour split en mots
        if (mode === 'words') {
            const splitTextNodeToWords = (node) => {
                const raw = node.nodeValue || '';
                // Split en mots et espaces (capturés)
                const parts = raw.split(/(\s+)/);

                parts.forEach((part) => {
                    if (part.trim() === '') {
                        // espace, on remet en texte
                        node.parentNode.insertBefore(document.createTextNode(part), node);
                    } else {
                        const outer = document.createElement('span');
                        outer.className = 'split-frag';

                        const inner = document.createElement('span');
                        inner.className = 'split-frag-i';
                        inner.setAttribute('aria-hidden', 'true');
                        inner.textContent = part;

                        outer.appendChild(inner);
                        node.parentNode.insertBefore(outer, node);
                        frags.push(inner);
                    }
                });
                node.remove();
            };

            const walkWords = (root) => {
                Array.from(root.childNodes).forEach((child) => {
                    if (child.nodeType === Node.TEXT_NODE) {
                        splitTextNodeToWords(child);
                    } else if (child.nodeType === Node.ELEMENT_NODE) {
                        if (child.classList && child.classList.contains('br')) return;
                        walkWords(child);
                    }
                });
            };

            walkWords(el);
            return frags;
        }

        // Par défaut 'chars'
        const splitTextNodeToChars = (node) => {
            const raw = node.nodeValue || '';
            const chars = Array.from(raw);

            chars.forEach((ch) => {
                // Laisser les espaces tels quels (pas de wrap)
                if (!/\S/.test(ch)) {
                    node.parentNode.insertBefore(document.createTextNode(ch), node);
                    return;
                }

                const outer = document.createElement('span');
                outer.className = 'split-frag'; // reste inline

                // Si le parent immédiat est un <strong> en display:block, on garde la césure visuelle
                const parentEl = node.parentNode;
                if (parentEl && parentEl.nodeType === Node.ELEMENT_NODE) {
                    if (
                        parentEl.tagName === 'STRONG' &&
                        getComputedStyle(parentEl).display === 'block'
                    ) {
                        outer.classList.add('split-frag--block');
                    }
                }

                const inner = document.createElement('span');
                inner.className = 'split-frag-i'; // animé en Y
                inner.setAttribute('aria-hidden', 'true');
                inner.textContent = ch;

                outer.appendChild(inner);
                node.parentNode.insertBefore(outer, node);
                frags.push(inner);
            });

            node.remove();
        };

        const walkChars = (root) => {
            Array.from(root.childNodes).forEach((child) => {
                if (child.nodeType === Node.TEXT_NODE) {
                    splitTextNodeToChars(child);
                } else if (child.nodeType === Node.ELEMENT_NODE) {
                    if (child.classList && child.classList.contains('br')) return;
                    walkChars(child);
                }
            });
        };

        walkChars(el);
        return frags;
    }

    // Helpers publics pour cohérence avec l'ancienne API
    killAll() {
        ScrollTrigger.killAll();
    }
    refresh() {
        ScrollTrigger.refresh();
    }
    update() {
        ScrollTrigger.update();
    }
}

export default {
    init() {
        const scopes = document.querySelectorAll(`[${ATTR_SCOPE}]`);
        if (!scopes.length) return;

        window.Anim.scroll.instances = [];

        scopes.forEach((scope) => {
            const instance = new ScrollEffects({ scope });
            instance.init();
            window.Anim.scroll.instances.push(instance);
        });

        // Fallback global: parallax sans dépendre de data-anim-scope
        const allParallax = Array.from(document.querySelectorAll(`[${ATTR_PARALLAX}]`));
        if (allParallax.length) {
            // Exclure ceux déjà pris en charge par les instances de scope
            const handled = new Set();
            window.Anim.scroll.instances.forEach((inst) => {
                const inScope = inst.scopeEl
                    ? Array.from(inst.scopeEl.querySelectorAll(`[${ATTR_PARALLAX}]`))
                    : [];
                inScope.forEach((el) => handled.add(el));
            });
            const orphans = allParallax.filter((el) => !handled.has(el));
            if (orphans.length) {
                const orphanInstance = new ScrollEffects({ scope: document.body });
                // réutilise la méthode interne pour éviter la duplication
                orphanInstance.initParallaxInside({ querySelectorAll: () => orphans });
                window.Anim.scroll.instances.push(orphanInstance);
            }
        }
    },
};
